/**
 * Shared Project Interface/Types
 */
import type { useStatsigClient } from "@statsig/react-bindings";
import type { ArticleImageProps } from "@bloomberg/media-ui-article/types/ArticleImage";
import type { SecurityListSecurity } from "@bloomberg/media-ui-markets/types/SecurityList";
import {
    BannerData,
    MenuItems,
    SubmenuItem,
} from "@bloomberg/media-ui-nav/dist/types";
import { type CmpClient } from "@foundation/cmp-client";
import { WSSTag } from "@foundation/metadata";
import { CommonNode } from "@foundation/rich-text-react-renderer";
import { Block } from "@foundation/rich-text-react-renderer/lib/@types";
import { TophatResponse } from "@foundation/tophat-client/lib/types";

import {
    AspectRatio,
    Collections as WardenclyffeCollections,
    Story as StoryRaw,
} from "~types/wardenclyffe";

export enum MEDIA_SUBTYPES {
    AUDIO = "audio",
    CHART = "chart",
    PHOTO = "photo",
    VIDEO = "video",
}

export enum ENTITY_SUBTYPES {
    PERSON = "person",
    SECURITY = "security",
    STORY = "story",
}

export enum SITES {
    BLOOMBERG = "Bloomberg",
    BLOOMBERG_CO_JP = "Bloomberg.co.jp",
}

export interface AdTargetingProps {
    adCode: string;
    page: string;
    record?: string;
}

type VideoStream = {
    format: string;
    type: string;
    url: string;
};

type ImageTheme = {
    id: string;
    url: string;
};

export type Image = {
    alt?: string;
    id: string;
    caption?: string;
    credit?: string;
    height: number;
    origHeight: number;
    origWidth: number;
    size?: string;
    themes?: ImageTheme[];
    title?: string;
    type: string;
    url: string;
    width: number;
};

export type Video = {
    aspectRatio?: AspectRatio;
    autoplay: boolean;
    avmmIds?: string[];
    bmmrId: string;
    caption?: string;
    compressedIds?: string;
    credit?: string;
    duration: number;
    id: string;
    prerollAdUnit: string;
    showName?: string;
    streams?: VideoStream[];
    thumbnail?: Image;
    title: string;
    transcript: string;
    type: string;
    url: string;
};

export type Person = {
    id: string;
    editorialBoard?: boolean;
    profileUrl?: string;
    name: string;
    thumbnail?: Image;
    twitter?: string;
    bio?: string;
    photoDisplayed: boolean;
    roles?: string[];
    slug?: string;
};

type StoryMetadata = {
    isMetered: boolean;
    cobrand?: string;
    magazine?: boolean;
    newsletterSlug?: string;
    suppressComments?: boolean;
    excludeFromPaywall?: boolean;
    theme?: string;
    background?: string;
    newsletterToutLabel?: string;
    terminalBlogId?: string;
    registrationWall?: boolean;
};

export type Storythread = {
    id: string;
    url: string;
    headline: string;
    label?: string;
};

export type Audio = {
    allowedOnWeb: boolean;
    description: string;
    duration: number;
    id: string;
    image?: Image;
    publishedAt: string;
    rawUrl: string;
    title: string;
    type: string;
    url: string;
};

export type ContextualNavLink = {
    title: string;
    story?: Story | null;
    customUrl?: string | null;
};

export type BlensTickers = SecurityListSecurity[];

export type Language = "en" | "ja" | "de" | "es" | "pt";

export type Story = {
    abstract?: string[];
    adTargeting: AdTargetingProps | null;
    aiSummary?: AISummary;
    archived?: boolean;
    assistance?: Person[];
    attributor?: string;
    authoredRegion: string;
    authors?: Person[];
    audios?: Audio[];
    blensSecurities: string[];
    body: Block;
    brand: string;
    byline?: string;
    categories?: string[];
    charts: any;
    checksum?: string;
    cobrand?: string;
    collections?: StoryCollections;
    contentTags?: WSSTag[];
    corrected?: boolean;
    disableAds?: boolean;
    disclaimer?: string;
    editorialBoard?: Person[];
    facebook?: { [key: string]: string };
    footer?: CommonNode;
    footnotes?: CommonNode;
    franchise?: string;
    headline: string;
    hedAndDekPosition?: string;
    id: string;
    imageAttachments?: { [key: string]: Image };
    isContextualNavViewTracked: boolean;
    isPageTracked: boolean;
    isTrending?: boolean;
    thumbnail?: Image;
    json: any;
    label: string;
    language?: Language;
    lede: Image | Video;
    ledeImageUrl?: string;
    locale: string;
    magazineIssue?: MagazineIssue;
    metadata?: StoryMetadata;
    mostRelevantTags: string[];
    moved: boolean;
    nicodes?: string[];
    opinionArticleType?: string;
    pillar?: string;
    premium?: boolean;
    publishedAt: string;
    resourceType?: string;
    revision?: string;
    readingUrl?: string;
    readingDuration?: number;
    secondaryBrands?: string[];
    securityContentTags?: Ticker[];
    seoCanonical: string;
    seoDescription?: string;
    seoHeadline: string;
    seoTitle: string;
    site: string;
    sites: string[];
    slug: string;
    socialDescription: string;
    socialHeadline: string;
    socialImageUrl: string;
    socialStatus?: string;
    storythreads?: Storythread[];
    summary?: string;
    summaryText?: string;
    summaryHtml?: string;
    terminalTickers?: Ticker[];
    trashline?: string;
    type?: ARTICLE_TYPE;
    twitterSocialHeadline: string;
    twitterTitle: string;
    twitterDescription: string;
    updatedAt: string;
    url: string;
    videoAttachments?: { [key: string]: Video };
    translationLanguage?: TRANSLATION_LANGUAGES;
    translationErrors?: any;
    tags?: Tag[];
};

export enum TRANSLATION_LANGUAGES {
    EN = "EN",
    JA = "JA",
    FR = "FR",
    DE = "DE",
    ES = "ES",
    PT = "PT",
    IT = "IT",
    ZT = "ZT",
    KO = "KO",
    ZH = "ZH",
}

export interface TranslatedStoryFields {
    revision: StoryRaw["revision"];
    headline: StoryRaw["headline"];
    lede: StoryRaw["lede"];
    summary: StoryRaw["summary"];
    docXml: StoryRaw["docXml"];
    abstract: StoryRaw["abstract"];
    trashline: StoryRaw["trashline"];
    translationLanguage: string;
}

export interface MagazineIssue {
    id: string;
    displayDate?: string;
    date: string;
    coverImage: Image;
}

export interface AISummary {
    text: string[];
    appliedAt: string;
}

export interface StoryCollections {
    newsletterEntitlements: Pick<
        WardenclyffeCollections,
        "newsletterEntitlements"
    > | null;
    record?: StoryCollection | null;
    recordId?: string;
    weekend?: StoryCollection | null;
    weekendId?: string;
}

export interface StoryCollection {
    title: string;
    titleLink?: string;
    slug: string;
    navLinks: (ContextualNavLink | null)[];
}

export interface Ticker {
    id: string;
}

export interface Tag {
    name: string;
}

export interface MostReadStory {
    brand: string;
    headline: string;
    id: string;
    thumbnail?: ArticleImageProps;
    url: string;
}

export interface PopularInOpinionStory {
    author: string;
    headline: string;
    id: string;
    thumbnailUrl?: string;
    url: string;
}

export interface ArticleInfo {
    contentId: string;
    resourceType: string;
    type?: string;
    brand?: string;
    franchise?: string;
    locale?: string;
    archived?: boolean;
    revision?: string;
    premium?: boolean;
    metadata?: {
        cobrand?: string;
        isMetered: boolean;
        registrationWall?: boolean;
        collections?: Pick<StoryCollections, "newsletterEntitlements">;
    };
}

export type PaywallType = "reductive" | "additive";

interface PaywallData {
    type?: PaywallType;
    selector?: string;
}

export interface FortressOptions {
    articleInfo?: ArticleInfo;
    paywallData?: PaywallData;
}

export interface InlineRecircTaxonomy {
    name: string;
    url: string;
    site?: string;
    enabled: boolean;
    cobrandEnabled: boolean;
}

export interface InlineRecircStory {
    id: string;
    brand: string;
    franchise?: string;
    site: string;
    headline: string;
    url: string;
}

export interface InlineRecirc {
    stories: InlineRecircStory[];
    prefix: string;
    index: string;
    brand: { name: string; url: string };
    cobrand?: { name: string; url: string };
}

// Add more as needed
export type PlugConfigKey =
    | "top-of-content"
    | "middle-of-content"
    | "bottom-of-content"
    | "left-rail";
export type PlugConfig = {
    [K in PlugConfigKey]?: string;
};

type BrandMetricsCmd = {
    id?: string;
    cmd: string;
    val?:
        | {
              event: "surveyanswered";
              handler: (arg: { mid: string; answers: string }) => void;
          }
        | {
              event: "surveyloaded";
              handler: (arg: {
                  mid: string;
                  available: boolean;
                  showed: boolean;
              }) => void;
          }
        | {
              event: "surveyrendered";
              handler: (arg: { mid: string }) => void;
          }
        | {
              callback(...args: any[]): void;
          };
};

declare global {
    interface Window {
        _brandmetrics: BrandMetricsCmd[];
        _regUserInfo?: {
            sessionId: string;
            userId?: string;
            subscription?: {
                type: string;
            };
        };
        plugClient: {
            init: (config: PlugConfig) => void;
        };
        newrelic: {
            addPageAction(
                actionName: string,
                customAttributes?: Record<string, any>
            ): void;
            noticeError: any;
        };
        NREUM?: {
            noticeError: (
                err: Error,
                attributes: Record<string, unknown>
            ) => void;
        };
        initializeAbbaScript: any;
        getCmpClient: () => CmpClient;
        cmpClient: {
            cmd: (() => void)[];
        };
        statsig: ReturnType<typeof useStatsigClient>;
    }
}

export interface ArticleRecircStory {
    byline: string;
    headline: string;
    id: string;
    image: Omit<Image, "url"> & { src: string };
    url: string;
}

export enum ARTICLE_TYPE {
    BASIC = "article",
    FEATURE = "feature",
    SLIDESHOW = "slideshow",
    NEWSLETTER = "newsletter_campaign",
    LETTER = "letter",
    QUICKTAKE = "quicktake",
    BREAKING_NEWS = "breaking_news",
    EXPLAINERS = "explainers",
}

export enum OPINION_ARTICLE_TYPE {
    MRB = "MRB",
    EDITORIAL_BOARD = "EDITORIAL_BOARD",
    GUEST_ESSAY = "GUEST_ESSAY",
    COLUMNIST = "COLUMNIST",
}

export type OpinionArticleData = {
    isInlineRecircEnabled: boolean;
};

export interface ArticleComponentProps {
    story: Story;
}

export interface LayoutProps {
    tophat: TophatResponse;
    menuItems: MenuItems | null;
    footerMenuItems: Record<string, SubmenuItem>;
    breakingNews: BannerData;
}

export function getWeekendCollectionId(x: Story): string | null {
    if (!x.collections || !x.collections.weekendId) return null;

    return x.collections.weekendId;
}

export function getWeekendCollection(x: Story): StoryCollection | null {
    if (!x.collections || !x.collections.weekend) return null;

    return x.collections.weekend;
}

export function getRecordCollection(x: Story): StoryCollection | null {
    if (!x.collections || !x.collections.record) return null;

    return x.collections.record;
}

export function getAnyCollection(x: Story): StoryCollection | null {
    return getWeekendCollection(x) || getRecordCollection(x);
}
